import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Upload, Info } from "lucide-react";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from "recharts";
import { runsApi } from "../api/endpoints";
import { SkeletonCard } from "../components/states/Skeleton";
import { ErrorState } from "../components/states/ErrorState";
import { EmptyState } from "../components/states/EmptyState";
import { FeasibilityBadge } from "../components/chips/FeasibilityBadge";
import { SeverityChip } from "../components/chips/SeverityChip";
import { FactChip } from "../components/chips/FactChip";
import { FEASIBILITY_META, SEVERITY_META, SEVERITY_ORDER } from "../lib/constants";
import { formatDateTime } from "../lib/format";

const STATUS_KEYS = ["FEASIBLE", "AT_RISK", "NOT_FEASIBLE", "INSUFFICIENT_DATA"];

function InfoTip({ children }) {
  return (
    <span
      className="ml-1 inline-flex items-center text-slate-400 cursor-help"
      title={children}
      aria-label={children}
    >
      <Info size={12} />
    </span>
  );
}

function SectionCard({ title, tooltip, children, testId }) {
  return (
    <section
      data-testid={testId}
      className="rounded border border-slate-200 bg-white p-5"
    >
      <header className="mb-4 flex items-center">
        <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
        {tooltip && <InfoTip>{tooltip}</InfoTip>}
      </header>
      {children}
    </section>
  );
}

export default function DashboardPage() {
  const { workspaceId } = useParams();
  const [runs, setRuns] = useState(null);
  const [run, setRun] = useState(null);
  const [findings, setFindings] = useState(null);
  const [processes, setProcesses] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);
    runsApi.list(workspaceId)
      .then(async (list) => {
        if (!mounted) return;
        setRuns(list || []);
        const latest = (list || []).find((r) => r.status === "completed");
        if (!latest) {
          setLoading(false);
          return;
        }
        const [runDoc, findingsDoc, procs] = await Promise.all([
          runsApi.get(workspaceId, latest.id),
          runsApi.findings(workspaceId, latest.id, { page_size: 200 }),
          runsApi.processes(workspaceId, latest.id),
        ]);
        if (!mounted) return;
        setRun(runDoc);
        setFindings(findingsDoc);
        setProcesses(procs);
        setLoading(false);
      })
      .catch((e) => {
        if (!mounted) return;
        setError(e);
        setLoading(false);
      });
    return () => { mounted = false; };
  }, [workspaceId]);

  if (loading) {
    return (
      <div className="space-y-6">
        <SkeletonCard />
        <div className="grid gap-6 lg:grid-cols-2">
          <SkeletonCard />
          <SkeletonCard />
        </div>
      </div>
    );
  }
  if (error) {
    return <ErrorState error={error} onRetry={() => window.location.reload()} />;
  }
  if (!run) {
    return (
      <EmptyState
        title="No completed analyses yet"
        description="Import your BIA data to see recovery-feasibility findings across your workspace."
        icon={<Upload size={32} />}
        action={
          <Link
            to={`/w/${workspaceId}/import`}
            data-testid="dashboard-empty-cta"
            className="rounded bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
          >
            Import your BIAs
          </Link>
        }
      />
    );
  }

  const statusCounts = run.summary?.overall_status_counts || {};
  const totalProcesses = Object.values(statusCounts).reduce((a, b) => a + b, 0);
  // Findings are aggregated on the client — summary doesn't include severity
  // or category counters; the engine tracks those on per-finding docs.
  const findingItems = findings?.items || [];
  const severityCounts = tally(findingItems, "effective_severity");
  const categoryCounts = tally(findingItems, "category");

  const donutData = STATUS_KEYS
    .map((k) => ({ name: FEASIBILITY_META[k].label, key: k, value: statusCounts[k] || 0 }))
    .filter((d) => d.value > 0);

  const severityData = SEVERITY_ORDER
    .map((k) => ({ name: SEVERITY_META[k].label, key: k, value: severityCounts[k] || 0 }))
    .filter((d) => d.value > 0);

  const categoryData = Object.entries(categoryCounts)
    .map(([k, v]) => ({ name: k, value: v }))
    .sort((a, b) => b.value - a.value);

  const topRisks = findingItems
    .filter((f) => f.scope === "process" && ["CRITICAL", "HIGH"].includes(f.effective_severity))
    .slice(0, 8);

  const dataCompleteness = processes && processes.length
    ? averagePct(processes, "data_completeness_pct")
    : null;
  const completedProcesses = (processes && processes.length) || totalProcesses;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">
            Latest analysis
          </div>
          <h1
            className="mt-1 text-2xl font-semibold text-slate-900"
            data-testid="dashboard-run-name"
          >
            {run.name}
          </h1>
          <div className="mt-1 text-sm text-slate-500">
            Completed {formatDateTime(run.created_at)} · {totalProcesses} processes
          </div>
        </div>
        <Link
          to={`/w/${workspaceId}/analyses/${run.id}`}
          data-testid="dashboard-open-run"
          className="rounded bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
        >
          Open run detail
        </Link>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <SectionCard
          title="Recovery Assurance status"
          tooltip="Distribution of overall recovery-feasibility statuses across all processes analysed in this run. Statuses are derived by the deterministic engine per PRD §12."
          testId="dashboard-status"
        >
          <div className="grid grid-cols-2 items-center gap-4">
            <div className="h-32">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    dataKey="value"
                    data={donutData}
                    innerRadius={38}
                    outerRadius={56}
                    stroke="#fff"
                    strokeWidth={2}
                  >
                    {donutData.map((d) => (
                      <Cell key={d.key} fill={statusColour(d.key)} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <ul className="space-y-1 text-sm">
              {STATUS_KEYS.map((k) => (
                <li key={k} className="flex items-center justify-between gap-2">
                  <FeasibilityBadge status={k} />
                  <span
                    className="font-mono text-slate-800"
                    data-testid={`dashboard-status-${k.toLowerCase()}`}
                  >
                    {statusCounts[k] || 0}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </SectionCard>

        <SectionCard
          title="Findings by severity"
          tooltip="Count of triggered findings by effective severity. Effective severity may differ from base severity when the engine escalates (e.g. critical-process context)."
          testId="dashboard-severity"
        >
          <div className="h-40">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={severityData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" fontSize={11} />
                <YAxis fontSize={11} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="value">
                  {severityData.map((d) => (
                    <Cell key={d.key} fill={severityColour(d.key)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>

        <SectionCard
          title="Findings by category"
          tooltip="Breakdown by rule category (BIA_QUALITY at present; will expand in future engine versions)."
          testId="dashboard-category"
        >
          <div className="h-40">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={categoryData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" fontSize={11} allowDecimals={false} />
                <YAxis type="category" dataKey="name" fontSize={11} width={110} />
                <Tooltip />
                <Bar dataKey="value" fill="#0f172a" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <SectionCard
            title="Top recovery risks"
            tooltip="Processes ranked by highest-severity open finding. Click a row for the full derivation trace."
            testId="dashboard-top-risks"
          >
            {topRisks.length === 0 ? (
              <div className="text-sm text-slate-500">
                No critical or high findings in this run.
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {topRisks.map((f) => (
                  <li
                    key={f.id}
                    className="flex items-center justify-between gap-3 py-2.5"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <SeverityChip severity={f.effective_severity} />
                        <FactChip source="FINDING" />
                        <Link
                          to={`/w/${workspaceId}/analyses/${run.id}/processes/${f.process_id}`}
                          data-testid={`dashboard-risk-link-${f.id}`}
                          className="text-sm font-medium text-slate-900 hover:underline truncate"
                        >
                          {f.process_id}
                        </Link>
                      </div>
                      <div className="mt-0.5 text-xs text-slate-500 truncate">
                        {f.calculation}
                      </div>
                    </div>
                    <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] text-slate-700 shrink-0">
                      {f.rule_id}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>
        </div>

        <SectionCard
          title="Data quality"
          tooltip="How much of the requested BIA data is populated across all processes in this run. Missing data becomes NOT_EVALUATED in the engine, not silent PASS."
          testId="dashboard-data-quality"
        >
          <dl className="space-y-3 text-sm">
            <div>
              <dt className="text-slate-500">Processes analysed</dt>
              <dd className="mt-0.5 text-xl font-semibold text-slate-900">
                {completedProcesses}
              </dd>
            </div>
            <div>
              <dt className="text-slate-500">Average data completeness</dt>
              <dd className="mt-0.5 text-xl font-semibold text-slate-900">
                {dataCompleteness === null ? "—" : `${dataCompleteness.toFixed(0)}%`}
              </dd>
            </div>
            <div>
              <dt className="text-slate-500">Open critical / high findings</dt>
              <dd className="mt-0.5 text-xl font-semibold text-slate-900">
                {(severityCounts.CRITICAL || 0) + (severityCounts.HIGH || 0)}
              </dd>
            </div>
          </dl>
        </SectionCard>
      </div>
    </div>
  );
}

function averagePct(list, key) {
  const vals = list.map((x) => x[key]).filter((v) => v !== null && v !== undefined);
  if (vals.length === 0) return null;
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

function tally(list, key) {
  const acc = {};
  for (const x of list) {
    const v = x[key];
    if (!v) continue;
    acc[v] = (acc[v] || 0) + 1;
  }
  return acc;
}

function statusColour(k) {
  switch (k) {
    case "FEASIBLE": return "#10b981";
    case "AT_RISK": return "#f59e0b";
    case "NOT_FEASIBLE": return "#ef4444";
    case "INSUFFICIENT_DATA": return "#94a3b8";
    default: return "#cbd5e1";
  }
}

function severityColour(k) {
  switch (k) {
    case "CRITICAL": return "#dc2626";
    case "HIGH": return "#ea580c";
    case "MEDIUM": return "#d97706";
    case "LOW": return "#64748b";
    case "REVIEW": return "#0284c7";
    default: return "#94a3b8";
  }
}
